import React, { useEffect, useState } from 'react';
import { Box, Fab, IconButton, Zoom } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';

const DISMISS_KEY = 'pwaInstallDismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWAButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalada
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      setShowButton(false);
      return;
    }

    // Si el usuario lo cerró antes, no volver a mostrarlo
    if (localStorage.getItem(DISMISS_KEY) === '1') {
      setShowButton(false);
      return;
    }

    // Escuchar el evento beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Ocultar el botón si la app se instala
    window.addEventListener('appinstalled', () => {
      setShowButton(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();

    // Esperar la respuesta del usuario
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('Usuario aceptó instalar la PWA');
    } else {
      console.log('Usuario rechazó instalar la PWA');
    }

    // Limpiar el prompt
    setDeferredPrompt(null);
    setShowButton(false);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowButton(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  return (
    <Zoom in={showButton}>
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
        <Fab color="primary" aria-label="Instalar app" onClick={handleInstallClick}>
          <GetAppIcon />
        </Fab>
        <IconButton
          aria-label="Cerrar"
          size="small"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            bgcolor: 'grey.700',
            color: 'common.white',
            '&:hover': { bgcolor: 'grey.900' },
            width: 22,
            height: 22,
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Zoom>
  );
};

export default InstallPWAButton;
